/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MultimodalLiveAPIClientConnection,
  MultimodalLiveClient,
} from "../lib/multimodal-live-client";
import { LiveConfig } from "../multimodal-live-types";
import { AudioStreamer } from "../lib/audio-streamer";
import { audioContext } from "../lib/utils";
import VolMeterWorket from "../lib/worklets/vol-meter";

export type LiveConnectionStatus =
  | "idle"
  | "connecting"
  | "live"
  | "disconnecting";

export type UseLiveAPIResults = {
  client: MultimodalLiveClient;
  setConfig: (config: LiveConfig) => void;
  config: LiveConfig;
  connected: boolean;
  connectionStatus: LiveConnectionStatus;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  volume: number;
  modelSpeaking: boolean;
};

export function useLiveAPI({
  url,
  apiKey,
}: MultimodalLiveAPIClientConnection): UseLiveAPIResults {
  const client = useMemo(
    () => new MultimodalLiveClient({ url, apiKey }),
    [url, apiKey],
  );
  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  const [connected, setConnected] = useState(false);
  const [config, setConfig] = useState<LiveConfig>({
    model: "models/gemini-2.5-flash-native-audio-preview-12-2025",
  });
  const [volume, setVolume] = useState(0);
  const [modelSpeaking, setModelSpeaking] = useState(false);
  const [connectionStatus, setConnectionStatus] =
    useState<LiveConnectionStatus>("idle");

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
          });
      });
    }
  }, [audioStreamerRef]);

  useEffect(() => {
    const onOpen = () => {
      setConnectionStatus((current) =>
        current === "disconnecting" ? "disconnecting" : "connecting",
      );
    };

    const onSetupComplete = () => {
      setConnectionStatus("live");
    };

    const onClose = () => {
      setConnected(false);
      setConnectionStatus("idle");
      setModelSpeaking(false);
    };

    const stopAudioStreamer = () => {
      audioStreamerRef.current?.stop();
      setModelSpeaking(false);
    };

    const onAudio = (data: ArrayBuffer) => {
      setModelSpeaking(true);
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));
    };

    client
      .on("open", onOpen)
      .on("setupcomplete", onSetupComplete)
      .on("close", onClose)
      .on("interrupted", stopAudioStreamer)
      .on("audio", onAudio);

    return () => {
      client
        .off("open", onOpen)
        .off("setupcomplete", onSetupComplete)
        .off("close", onClose)
        .off("interrupted", stopAudioStreamer)
        .off("audio", onAudio);
    };
  }, [client]);

  useEffect(() => {
    const streamer = audioStreamerRef.current;
    if (!streamer) return;

    streamer.onComplete = () => {
      setModelSpeaking(false);
    };
  }, [audioStreamerRef, connected]);

  const connect = useCallback(async () => {
    if (connectionStatus === "connecting" || connectionStatus === "live") {
      return;
    }

    setConnectionStatus("connecting");

    try {
      client.disconnect();
      await client.connect(config);
      setConnected(true);
    } catch (error) {
      setConnected(false);
      setConnectionStatus("idle");
      throw error;
    }
  }, [client, setConnected, config, connectionStatus]);

  const disconnect = useCallback(async () => {
    if (connectionStatus === "idle" || connectionStatus === "disconnecting") {
      return;
    }

    setConnectionStatus("disconnecting");
    const disconnected = client.disconnect();
    setConnected(false);

    if (!disconnected) {
      setConnectionStatus("idle");
    }
  }, [setConnected, client, connectionStatus]);

  return {
    client,
    config,
    setConfig,
    connected: connectionStatus === "live" && connected,
    connectionStatus,
    connect,
    disconnect,
    volume,
    modelSpeaking,
  };
}
